import { useState, useCallback, useRef } from 'react';
import { useStore, type CustomFieldDefinition, type CustomFieldType } from '../store/useStore';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { Plus, Trash2, Edit2, GripVertical, FileText, AlertTriangle } from 'lucide-react';
import { ConfigModal } from './ConfigModal';

const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  textbox: 'Texte',
  number: 'Nombre',
  date: 'Date',
  radio: 'Choix multiples',
  checkbox: 'Case à cocher',
};

const EMPTY_FORM = {
  label: '',
  fieldType: 'textbox' as CustomFieldType,
  options: [] as string[],
  required: false,
  sortOrder: 0,
};

export function CustomFieldsConfig() {
  const { customFieldDefinitions, currentOrgId, addCustomFieldDefinition, updateCustomFieldDefinition, deleteCustomFieldDefinition, reorderCustomFieldDefinitions } = useStore();
  const { currentUser } = useVisibleStore();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [initialData, setInitialData] = useState(EMPTY_FORM);
  const [modalOpen, setModalOpen] = useState(false);
  const [optionsText, setOptionsText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const dragItem = useRef<string | null>(null);
  const dragOverItem = useRef<string | null>(null);

  const isAdmin = currentUser.role === 'admin' || (currentUser.role as string) === 'superadmin';

  // Filter fields for current org, sorted by sortOrder
  const orgFields = customFieldDefinitions
    .filter(f => f.orgId === currentOrgId)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData);

  const showStatus = (text: string, type: 'success' | 'error') => {
    setStatusMessage({ text, type });
    setTimeout(() => setStatusMessage(null), 3000);
  };

  const openNew = () => {
    const sorted = orgFields;
    const nextOrder = sorted.length > 0 ? sorted[sorted.length - 1].sortOrder + 1 : 0;
    const defaults = { ...EMPTY_FORM, sortOrder: nextOrder };
    setFormData(defaults);
    setInitialData(defaults);
    setOptionsText('');
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (field: CustomFieldDefinition) => {
    const data = {
      label: field.label,
      fieldType: field.fieldType,
      options: field.options || [],
      required: field.required,
      sortOrder: field.sortOrder,
    };
    setFormData(data);
    setInitialData(data);
    setOptionsText((field.options || []).join('\n'));
    setEditingId(field.id);
    setModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setInitialData(EMPTY_FORM);
    setOptionsText('');
  }, []);

  const handleSave = () => {
    if (!formData.label.trim()) {
      showStatus('Veuillez saisir un libellé pour le champ.', 'error');
      return;
    }

    const parsedOptions = formData.fieldType === 'radio'
      ? optionsText.split('\n').map(o => o.trim()).filter(o => o.length > 0)
      : [];

    if (formData.fieldType === 'radio' && parsedOptions.length < 2) {
      showStatus('Veuillez saisir au moins 2 options pour le champ à choix multiples.', 'error');
      return;
    }

    const payload = {
      ...formData,
      options: parsedOptions,
    };

    if (editingId) {
      updateCustomFieldDefinition(editingId, payload);
      showStatus('Champ mis à jour avec succès.', 'success');
    } else {
      addCustomFieldDefinition(payload);
      showStatus('Champ ajouté avec succès.', 'success');
    }
    closeModal();
  };

  const handleDelete = (id: string) => {
    deleteCustomFieldDefinition(id);
    setConfirmDelete(null);
    showStatus('Champ supprimé avec succès.', 'success');
  };

  // ── Drag & Drop handlers ──

  const handleDragStart = (fieldId: string) => {
    dragItem.current = fieldId;
  };

  const handleDragOver = (e: React.DragEvent, fieldId: string) => {
    e.preventDefault();
    dragOverItem.current = fieldId;
  };

  const handleDrop = () => {
    const fromId = dragItem.current;
    const toId = dragOverItem.current;
    if (!fromId || !toId || fromId === toId) return;

    const orderedIds = orgFields.map(f => f.id);
    const fromIdx = orderedIds.indexOf(fromId);
    const toIdx = orderedIds.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return;

    orderedIds.splice(fromIdx, 1);
    orderedIds.splice(toIdx, 0, fromId);
    reorderCustomFieldDefinitions(orderedIds);

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const getTypeBadgeColor = (type: CustomFieldType) => {
    switch (type) {
      case 'textbox': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'number': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'date': return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20';
      case 'radio': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'checkbox': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 relative z-10">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0A0B0E]/95 backdrop-blur-sm border-b border-[#2D3139]/50 px-5 py-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Champs personnalisés</h2>
              <p className="text-xs text-slate-400">
                {orgFields.length > 0
                  ? `${orgFields.length} champ(s) défini(s) — glissez-déposez pour réorganiser`
                  : "Définissez les champs supplémentaires à collecter auprès des intervenants"}
              </p>
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={openNew}
              className="flex items-center space-x-2 px-4 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-indigo-500/20"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter un champ</span>
            </button>
          )}
        </div>
      </div>

      {/* Status message */}
      {statusMessage && (
        <div className={`mx-5 mt-3 px-4 py-3 rounded-xl text-sm font-medium ${
          statusMessage.type === 'success'
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
            : 'bg-red-500/10 border border-red-500/30 text-red-400'
        }`}>
          {statusMessage.text}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4">
        {orgFields.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-800/50 border border-[#2D3139] flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-slate-300 font-medium mb-1">Aucun champ personnalisé</h3>
            <p className="text-slate-500 text-sm max-w-sm">
              Ajoutez des champs comme "Taille de t-shirt", "Date de disponibilité" ou "Préférences" pour collecter des informations supplémentaires auprès de vos intervenants.
            </p>
            {isAdmin && (
              <button
                onClick={openNew}
                className="mt-6 flex items-center space-x-2 px-4 py-2.5 bg-[#1A1D23] hover:bg-[#252830] border border-[#2D3139] text-slate-300 rounded-xl text-sm transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Créer votre premier champ</span>
              </button>
            )}
          </div>
        ) : (
          /* Field list */
          <div className="space-y-2 max-w-2xl">
            {orgFields.map((field) => (
              <div
                key={field.id}
                draggable={isAdmin}
                onDragStart={() => handleDragStart(field.id)}
                onDragOver={(e) => handleDragOver(e, field.id)}
                onDrop={handleDrop}
                className={`group bg-[#14161B]/80 border border-[#2D3139] rounded-xl p-4 transition-all hover:border-slate-600 ${
                  isAdmin ? 'cursor-grab active:cursor-grabbing' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Drag handle */}
                  {isAdmin && (
                    <div className="text-slate-600 hover:text-slate-400 transition-colors flex-none cursor-grab active:cursor-grabbing">
                      <GripVertical className="w-4 h-4" />
                    </div>
                  )}

                  {/* Field info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium text-sm truncate">{field.label}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${getTypeBadgeColor(field.fieldType)}`}>
                        {FIELD_TYPE_LABELS[field.fieldType]}
                      </span>
                      {field.required && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                          Obligatoire
                        </span>
                      )}
                    </div>
                    {field.fieldType === 'radio' && field.options.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1.5 truncate">
                        Options : {field.options.join(', ')}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  {isAdmin && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEdit(field)}
                        className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
                        title="Modifier"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(field.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <ConfigModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Modifier le champ' : 'Ajouter un champ'}
        subtitle={editingId ? "Modifier les paramètres du champ personnalisé" : "Définir un nouveau champ à collecter"}
        icon={editingId ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        isDirty={isDirty}
      >
        <div className="p-6 space-y-5">
          {/* Label */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
              Libellé du champ
            </label>
            <input
              autoFocus
              type="text"
              value={formData.label}
              onChange={e => setFormData({ ...formData, label: e.target.value })}
              placeholder="Ex: Taille de t-shirt"
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
              Type de champ
            </label>
            <select
              value={formData.fieldType}
              onChange={e => setFormData({ ...formData, fieldType: e.target.value as CustomFieldType })}
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all appearance-none"
            >
              {(Object.entries(FIELD_TYPE_LABELS) as [CustomFieldType, string][]).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Radio options (only visible when type=radio) */}
          {formData.fieldType === 'radio' && (
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                Options de choix
              </label>
              <textarea
                value={optionsText}
                onChange={e => setOptionsText(e.target.value)}
                placeholder={"S\nM\nL\nXL"}
                rows={4}
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600 resize-none"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Saisissez une option par ligne (minimum 2).
              </p>
            </div>
          )}

          {/* Required toggle */}
          <div className="flex items-center justify-between bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3">
            <div>
              <p className="text-sm text-white font-medium">Obligatoire</p>
              <p className="text-xs text-slate-500">Le team devra obligatoirement remplir ce champ</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={e => setFormData({ ...formData, required: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-2 border-t border-[#2D3139]/50">
            {editingId ? (
              <button
                onClick={() => setConfirmDelete(editingId)}
                className="flex items-center space-x-1.5 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg text-sm transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer</span>
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center space-x-3">
              <button
                onClick={closeModal}
                className="px-4 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-emerald-500/20"
              >
                {editingId ? 'Enregistrer' : 'Ajouter le champ'}
              </button>
            </div>
          </div>
        </div>
      </ConfigModal>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">Supprimer ce champ ?</h3>
              <p className="text-slate-400 text-sm mb-6">
                Toutes les valeurs associées à ce champ seront également supprimées. Cette action est irréversible.
              </p>
              <div className="flex items-center space-x-3 w-full">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 px-4 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-sm font-medium transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(confirmDelete)}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
